// src/services/firebaseTaskService.js - With Real OpenAI Integration
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  where, 
  orderBy,    
  onSnapshot,
  getDocs,
  serverTimestamp
} from 'firebase/firestore';
  import { db } from '../config/firebase';
  import { openaiService } from './openaiService';
  
  class FirebaseTaskService {
    constructor() {
      this.collectionName = 'tasks';
    }
  
    // Get all tasks for current user with real-time updates (FIXED - no orderBy)
    subscribeToUserTasks(userId, onTasksUpdate, onError) {
      try {
        const tasksQuery = query(
          collection(db, 'tasks'),
          where('userId', '==', userId),
          orderBy('createdAt', 'desc')
        );
    
        const unsubscribe = onSnapshot(
          tasksQuery,
          (snapshot) => {
            const tasks = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data(),
              createdAt: doc.data().createdAt?.toDate() || new Date(),
              completedAt: doc.data().completedAt?.toDate() || null
            }));
            onTasksUpdate(tasks);
          },
          (error) => {
            console.error('Firestore subscription error:', error);
            if (onError) onError(error);
          }
        );
    
        return unsubscribe;
      } catch (error) {
        console.error('Error setting up task subscription:', error);
        if (onError) onError(error);
        return () => {}; // Return empty cleanup function
      }
    }
  
    // Get tasks once (no real-time updates) - FIXED
    async getUserTasks(userId) {
      try {
        const tasksRef = collection(db, this.collectionName);
        // Simplified query - no orderBy
        const q = query(
          tasksRef,
          where('userId', '==', userId)
        );
        
        const querySnapshot = await getDocs(q);
        const tasks = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          tasks.push({
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date()
          });
        });
        
        // Sort in JavaScript
        tasks.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        
        console.log('📋 Fetched', tasks.length, 'tasks for user');
        return tasks;
      } catch (error) {
        console.error('❌ Error fetching tasks:', error);
        throw error;
      }
    }
  
    // Create new task with REAL OpenAI analysis
    async createTask(userId, taskData) {
      try {
        console.log('🤖 Creating task with REAL OpenAI analysis...');
        
        // Get REAL AI analysis from OpenAI
        const aiAnalysis = await openaiService.analyzeTask(taskData);
        
        // Prepare task data
        const newTask = {
          title: taskData.title.trim(),
          description: taskData.description ? taskData.description.trim() : '',
          userId,
          priority: aiAnalysis.priority,
          priorityLevel: aiAnalysis.priorityLevel,
          aiAnalysis: {
            reasoning: aiAnalysis.reasoning,
            confidence: aiAnalysis.confidence,
            keywords: aiAnalysis.keywords,
            source: aiAnalysis.reasoning.includes('fallback') ? 'fallback' : 'openai'
          },
          status: 'pending',
          tags: taskData.tags || [],
          dueDate: taskData.dueDate ? new Date(taskData.dueDate) : null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };
        
        // Add to Firestore
        const docRef = await addDoc(collection(db, this.collectionName), newTask);
        
        return {
          id: docRef.id,
          ...newTask,
          createdAt: new Date(),
          updatedAt: new Date()
        };
      } catch (error) {
        console.error(' Error creating task:', error);
        throw error;
      }
    }
  
    // Update task with REAL OpenAI re-analysis
    async updateTask(taskId, updates, userId) {
      try {
        const taskRef = doc(db, this.collectionName, taskId);
        
        // Check if content changed (need AI re-analysis)
        const contentChanged = updates.title !== undefined || updates.description !== undefined;
        
        let updateData = {
          ...updates,
          updatedAt: serverTimestamp()
        };
        
        // Re-analyze with REAL OpenAI if content changed
        if (contentChanged) {
          const aiAnalysis = await openaiService.analyzeTask({
            title: updates.title,
            description: updates.description
          });
          
          updateData.priority = aiAnalysis.priority;
          updateData.priorityLevel = aiAnalysis.priorityLevel;
          updateData.aiAnalysis = {
            reasoning: aiAnalysis.reasoning,
            confidence: aiAnalysis.confidence,
            keywords: aiAnalysis.keywords,
            source: aiAnalysis.reasoning.includes('fallback') ? 'fallback' : 'openai'
          };
        }
        
        await updateDoc(taskRef, updateData);
        
        console.log('✅ Task updated with AI re-analysis:', taskId);
        
        return updateData;
      } catch (error) {
        console.error('❌ Error updating task:', error);
        throw error;
      }
    }
  
    // Delete task
    async deleteTask(taskId) {
      try {
        await deleteDoc(doc(db, this.collectionName, taskId));
        console.log('✅ Task deleted:', taskId);
      } catch (error) {
        console.error('❌ Error deleting task:', error);
        throw error;
      }
    }
  
    // Get tasks by priority for the priority view
    getTasksByPriority(tasks) {
      return {
        high: tasks.filter(task => task.priorityLevel === 'high'),
        medium: tasks.filter(task => task.priorityLevel === 'medium'),
        low: tasks.filter(task => task.priorityLevel === 'low')
      };
    }
  
    // Test OpenAI connection
    async testOpenAI() {
      return await openaiService.testConnection();
    }
  }
  
  export const firebaseTaskService = new FirebaseTaskService();